<script lang="ts">
  import { cn } from "$lib/utils";
  import { Box } from "lucide-svelte";
  import { authStore as Current } from "$lib/stores/authStore";
  import { type SearchResult, type SearchInfo, SEARCH_PROGRESS, type List } from "$lib/types";
  import SearchHeader from "$lib/components/search/searchHeader/searchHeader.svelte";
  import TableControls from "./tableControls.svelte";
  import ResultSidePanel from "./resultSidePanel.svelte";
  import SearchResultsTable from "./table.svelte";
  import { isDevelopment } from "$lib/utility/environment";
  import { sidePanelStore } from "$lib/stores/sidePanelStore";
  import { get } from "svelte/store";
  import { hotkeysStore } from "$lib/stores/hotkeysStore";
  import { searchFormOverlayStore } from "$lib/stores/searchFormOverlayStore";

  import { createEventDispatcher } from "svelte";
  import { authStore } from "$lib/stores";
  const dispatch = createEventDispatcher();

  let {
    allResultsDisplayed,
    subscriptionDialogOpen = $bindable(false),
    currentSearch,
    definiteMatchesCount = 0,
    displayedResults,
    isAllSelected = $bindable(false),
    isExportingPremiumCSV,
    isLoadingData,
    isLoadingMoreResults,
    isViewedByOwner,
    localFindMore,
    localSearchProgress = "",
    partialMatchesCount = 0,
    isSearchInProgress,
    queryToShow = "",
    recordType,
    selectedResults = $bindable(new Set<string>()),
    showDefiniteOnly = $bindable(false),
    type = "person",
    showSignInPrompt,
  } = $props<{
    subscriptionDialogOpen?: boolean;
    allResultsDisplayed?: boolean;
    currentSearch: SearchInfo | List;
    definiteMatchesCount?: number;
    displayedResults: SearchResult[];
    isExportingPremiumCSV?: boolean;
    isLoadingData?: boolean;
    isLoadingMoreResults?: boolean;
    isViewedByOwner?: boolean;
    localFindMore?: boolean;
    localSearchProgress?: string;
    partialMatchesCount?: number;
    queryToShow?: string;
    showDefiniteOnly?: boolean;
    isSearchInProgress?: boolean;
    recordType?: "search" | "list";
    type?: "person" | "company";
    showSignInPrompt?: () => void;
  }>();

  let currentSearchQuery = $state("");
  let currentSortDirection = $state<"asc" | "desc">("desc");
  let currentSortField = $state<string>("criteria");
  let filteredResults = $state<SearchResult[]>([]);
  let isPanelOpen = $state(false);
  let listRef = $state<HTMLElement | null>(null);
  let selectedIndex = $state<number | null>(null);
  let selectedResult = $state<SearchResult | null>(null);
  let searchResultsTableRef = $state<SearchResultsTable | null>(null);

  const hasMoreResultsToLoad = $derived(
    currentSearch &&
      (showDefiniteOnly
        ? (currentSearch.definiteMatch || 0) > currentSearch.results.length
        : (currentSearch.definiteMatch || 0) + (currentSearch.partialMatch || 0) > currentSearch.results.length),
  );

  function getFilteredAndSortedResults(
    results: SearchResult[],
    query: string,
    sort: { field: string; direction: "asc" | "desc" },
  ): SearchResult[] {
    if (!results?.length) return [];

    const needsFiltering = query.length > 0;

    // Pre-process the query once
    const lowercaseQuery = needsFiltering ? query.toLowerCase().trim() : "";

    // Create a memoized getter for sort values to avoid recalculating
    const getSortValue = (result: SearchResult): string | number => {
      switch (sort.field) {
        case "name":
          return `${result.firstName} ${result.lastName}`.toLowerCase();
        case "title":
          return (result.title || "").toLowerCase();
        case "companyName":
          return (result.companyName || "").toLowerCase();
        case "criteria":
          return (result.criteriaAndReasons || []).reduce((count, cr) => count + (cr.isMatch === "match" ? 1 : 0), 0);
        default:
          return "";
      }
    };

    // Calculate sort values once for each result
    const resultsWithSortValues = results.map((result) => ({
      result,
      sortValue: getSortValue(result),
      // Only calculate searchText if filtering is needed
      searchText: needsFiltering
        ? [result.firstName, result.lastName, result.title, result.companyName].filter(Boolean).join(" ").toLowerCase()
        : "",
    }));

    // Filter if needed
    const filtered = needsFiltering
      ? resultsWithSortValues.filter((item) => item.searchText.includes(lowercaseQuery))
      : resultsWithSortValues;

    // Sort with pre-calculated values
    const sortMultiplier = sort.direction === "asc" ? 1 : -1;
    const criteriaMultiplier = sort.field === "criteria" ? -1 : 1;
    const finalMultiplier = sortMultiplier * criteriaMultiplier;

    filtered.sort((a, b) => {
      const valueA = a.sortValue;
      const valueB = b.sortValue;

      return (
        finalMultiplier *
        (typeof valueA === "number" ? Number(valueB) - Number(valueA) : String(valueA).localeCompare(String(valueB)))
      );
    });

    // Return only the original result objects
    return filtered.map((item) => item.result);
  }

  function getResultKey(result: SearchResult): string {
    return result?.id;
  }

  $effect(() => {
    if (!isLoadingData && currentSearch) {
      const shouldBeOpen = isViewedByOwner && currentSearch;
      sidePanelStore.setOpen(shouldBeOpen);
    }
  });

  function handleRowSelection(result: SearchResult, index: number) {
    selectedResult = result;
    if (selectedIndex !== index) {
      selectedIndex = index;
      isPanelOpen = true;
    }
  }

  function handleCellSelection(result: SearchResult, rowIndex: number, cellIndex: number) {
    selectedResult = result;
    if (selectedIndex !== rowIndex) {
      selectedIndex = rowIndex;
      isPanelOpen = true;
    }
  }

  function handleKeyDown(e: KeyboardEvent) {
    if ($hotkeysStore.open || $searchFormOverlayStore.isVisible) return;
    if (!filteredResults.length) return;

    switch (e.key) {
      case " ":
        e.preventDefault();
        if (selectedIndex !== null) {
          isPanelOpen = !isPanelOpen;
        }
        break;

      case "Escape":
        e.preventDefault();
        isPanelOpen = false;
        break;
    }
  }

  function handleCSVDownload() {
    dispatch("csvDownload");
  }

  function handlePremiumCSVDownload(event: CustomEvent) {
    dispatch("premiumCsvDownload", event.detail);
  }

  function handleEmailUpdate(e: CustomEvent<{ email: string }>) {
    if (!selectedIndex) return;
    displayedResults[selectedIndex].email = e.detail.email;
    Current.updateUserCredits();
  }

  function handlePhoneUpdate(e: CustomEvent<{ phone: string }>) {
    if (!selectedIndex) return;
    displayedResults[selectedIndex].phone = e.detail.phone;
    Current.updateUserCredits();
  }

  $effect(() => {
    if (selectedIndex !== null) {
      const rows = document.querySelectorAll('tr[role="button"]');
      const selectedRow = rows[selectedIndex] as HTMLElement;
      if (selectedRow) {
        selectedRow.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }
  });

  $effect(() => {
    if (listRef && selectedIndex !== null) {
      const children = Array.from(listRef.children);
      const selectedElement = children[selectedIndex] as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }
  });

  $effect(() => {
    dispatch("panelStateChange", { isPanelOpen });
  });

  $effect(() => {
    const needsUpdate = displayedResults?.length > 0 && !isLoadingData;

    if (needsUpdate) {
      const newResults = getFilteredAndSortedResults(displayedResults, currentSearchQuery, {
        field: currentSortField,
        direction: currentSortDirection,
      });

      const currentLength = filteredResults.length;
      const newLength = newResults.length;
      const hasChanged =
        currentLength !== newLength || JSON.stringify(filteredResults[0]?.id) !== JSON.stringify(newResults[0]?.id);

      if (hasChanged) {
        logger.log("Updating filtered results:", {
          oldLength: currentLength,
          newLength,
          reason: "Results changed",
        });

        filteredResults = newResults;
      }
    }
  });

  function handleFetchMore() {
    dispatch("fetchMore");
  }

  function handleFindMore() {
    dispatch("findMore");
  }

  function handleFindAll() {
    dispatch("findAll");
  }
</script>

<svelte:window on:keydown={handleKeyDown} />
<div
  class={cn(
    "results-panel-wrapper flex min-w-0 flex-col gap-2 dark:bg-theme-background-tertiary",
    isPanelOpen ? "w-[calc(100%-360px)]" : "w-full",
  )}
>
  <SearchHeader
    bind:subscriptionDialogOpen
    {showDefiniteOnly}
    {recordType}
    {isViewedByOwner}
    {definiteMatchesCount}
    {partialMatchesCount}
    {localSearchProgress}
    {isLoadingMoreResults}
    searchQuery={currentSearch.query}
    searchCriteria={currentSearch.criteria}
    filteredResultsLength={displayedResults.length}
  />

  <div bind:this={listRef} class={cn("transition-default flex min-h-0 min-w-0 flex-1 flex-col space-y-2")}>
    <TableControls
      {isViewedByOwner}
      {isExportingPremiumCSV}
      {showSignInPrompt}
      {isLoadingData}
      {isSearchInProgress}
      {handleFetchMore}
      {handleFindMore}
      {handleFindAll}
      on:search={(e) => (currentSearchQuery = e.detail.query)}
      on:addColumn={(e) => {
        if (searchResultsTableRef) {
          searchResultsTableRef.addColumn();
        }
      }}
      on:csvDownload={handleCSVDownload}
      on:premiumCsvDownload={handlePremiumCSVDownload}
    />
    {#if !isLoadingData && currentSearch.results.length === 0 && currentSearch.status == "completed"}
      <div class="absolute inset-0 top-[40vh] flex h-full max-h-32 flex-col items-center justify-center gap-2">
        <Box size="64" strokeWidth="1" />
        <p class="text-center text-base text-theme-text-primary">
          <span class="font-semibold">No results</span><br />
          <span class="text-base">Try a different search</span>
        </p>
      </div>
    {:else}
      <!-- Table View -->
      <div class="custom-scrollbar min-h-0 flex-1 overflow-auto">
        <SearchResultsTable
          bind:this={searchResultsTableRef}
          bind:selectedIndex
          bind:currentSortField
          bind:currentSortDirection
          bind:selectedResults
          bind:isAllSelected
          bind:showDefiniteOnly
          {isLoadingMoreResults}
          {filteredResults}
          {isLoadingData}
          {isSearchInProgress}
          {type}
          {currentSearchQuery}
          {isPanelOpen}
          {isViewedByOwner}
          {hasMoreResultsToLoad}
          {handleFetchMore}
          upsellCallback={() => {
            subscriptionDialogOpen = true;
          }}
          on:rowSelect={(event) => handleRowSelection(event.detail.result, event.detail.index)}
          on:cellSelect={(event) =>
            handleCellSelection(event.detail.result, event.detail.rowIndex, event.detail.colIndex)}
        />
      </div>
    {/if}
  </div>

  <ResultSidePanel
    {isPanelOpen}
    {selectedResult}
    {type}
    {isViewedByOwner}
    upsellCallback={() => {
      subscriptionDialogOpen = true;
    }}
    onPanelClose={() => (isPanelOpen = false)}
  />
</div>
